import classNames from 'classnames'
import { CSSProperties, memo, ReactNode, useMemo } from 'react'

import type { UniqueIdentifier } from '@dnd-kit/core'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { DragIndicator } from '@mui/icons-material'
import { IconButton, SxProps, TableRow } from '@mui/material'
import { VirtualItem, Virtualizer } from '@tanstack/react-virtual'

import { GRTooltip } from '../../GRTooltip/GRTooltip'
import { BodyCell } from '../BodyCell/BodyCell'
import type { GRTableColumn } from '../GRTable'
import { findParentColumn, isLastVisibleColumnOfParent, getChildColumns, stickyColumnProps } from '../helpers/tableHelpers'
import './VirtualRow.scss'

/**
 * Component representing a virtual row in a virtualized table
 */

type VirtualizedRowProps<RowType, CustomPropsType, ColumnIdType = void> = {
  id: UniqueIdentifier
  virtualizer: Virtualizer<Window, Element> | Virtualizer<Element, Element> | Virtualizer<HTMLElement, Element>
  row: RowType
  virtualRow: VirtualItem & { style?: CSSProperties }
  columns: GRTableColumn<RowType, CustomPropsType, ColumnIdType>[]
  parentColumns?: GRTableColumn<RowType, CustomPropsType, ColumnIdType>[]
  getCellValue: ({ ...args }: { row: RowType; col: GRTableColumn<RowType, CustomPropsType, ColumnIdType>; rowIndex: number }) => ReactNode
  getColumnHiddenValue: (col: GRTableColumn<RowType, CustomPropsType, ColumnIdType>) => boolean
  hoverable?: boolean
  striped?: boolean
  style?: CSSProperties
  className?: string
  gridlines?: boolean
  sortable?: boolean
  disableSort?: boolean
  disableSortTooltip?: string
}

const RecursiveVirtualRow = <RowType extends object, CustomPropsType extends object | undefined, ColumnIdType extends string | void>({
  id,
  virtualizer,
  row,
  virtualRow,
  columns,
  parentColumns = [],
  getCellValue,
  getColumnHiddenValue,
  striped,
  style,
  hoverable,
  className,
  gridlines,
  sortable,
  disableSort,
  disableSortTooltip,
}: VirtualizedRowProps<RowType, CustomPropsType, ColumnIdType>) => {
  const { transform, setNodeRef, transition, isDragging, sortableColumns } = useSortableRow({ id, sortable, columns, disableSort, disableSortTooltip })
  const childColumns = getChildColumns(columns)
  const rowClassName = classNames('VirtualRow', className, { 'VirtualRow--even': striped && virtualRow.index % 2 === 0 })

  return childColumns.length > 0 ? (
    <RecursiveVirtualRow
      id={id}
      virtualizer={virtualizer}
      row={row}
      virtualRow={virtualRow}
      columns={childColumns}
      className={rowClassName}
      parentColumns={columns}
      getCellValue={getCellValue}
      getColumnHiddenValue={getColumnHiddenValue}
      striped={striped}
      style={style}
      hoverable={hoverable}
    />
  ) : (
    <TableRow
      style={{
        position: 'absolute',
        ...style,
        transform: CSS.Translate.toString(transform),
        transition,
        ...(isDragging ? { opacity: 0.5 } : {}),
        zIndex: isDragging ? 100 : 0,
        top: virtualRow.start - virtualizer.options.scrollMargin,
      }}
      data-index={virtualRow.index}
      ref={(node) => {
        virtualizer.measureElement(node)
        setNodeRef(node)
      }}
      hover={hoverable}
      className={rowClassName}
    >
      {sortableColumns.map((column, columnIndex) => {
        const rowIndex = virtualRow.index
        const isGrouping = isLastVisibleColumnOfParent(parentColumns, column, getColumnHiddenValue) || childColumns.length > 0
        const parentColumn = findParentColumn(parentColumns, column)
        const isStickyColumn = column.sticky || parentColumn?.sticky
        const cellProps = isStickyColumn ? { ...column.cellProps, sx: { ...column.cellProps?.sx, ...stickyColumnProps.sx } as SxProps } : column.cellProps
        const isHidden = (parentColumn && getColumnHiddenValue(parentColumn)) || getColumnHiddenValue(column)

        return isHidden ? null : (
          <BodyCell
            key={columnIndex}
            columnIndex={columnIndex}
            cellProps={cellProps}
            grouping={isGrouping || gridlines}
            style={{ width: column.width, minWidth: column.width, maxWidth: column.width, paddingRight: isStickyColumn ? 24 : undefined }}
          >
            {getCellValue({ row, col: column, rowIndex })}
          </BodyCell>
        )
      })}
    </TableRow>
  )
}

export const VirtualRow = memo(RecursiveVirtualRow) as typeof RecursiveVirtualRow

type UseSortableRowParams<RowType, CustomPropsType, ColumnIdType = void> = {
  id: UniqueIdentifier
  sortable?: boolean
  columns: GRTableColumn<RowType, CustomPropsType, ColumnIdType>[]
  disableSort?: boolean
  disableSortTooltip?: string
}

/**
 * Custom hook that adds sorting functionality to a table row.
 * When sorting is enabled, it prepends a drag handle column to the columns array.
 *
 * @template RowType - The type of data in each row
 * @template CustomPropsType - Custom properties that can be passed to the table
 * @template ColumnIdType - The type of column identifiers
 *
 * @param {Object} params - Hook parameters
 * @param {string | number} params.id - Unique identifier for the row
 * @param {boolean} params.sortable - Whether the row should be sortable
 * @param {GRTableColumn<RowType, CustomPropsType, ColumnIdType>[]} params.columns - Array of column definitions
 *
 * @returns {Object} Object containing sortable properties and methods
 * @returns {GRTableColumn<RowType, CustomPropsType, ColumnIdType>[]} returns.sortableColumns - Original columns with drag handle column prepended if sortable
 * @returns {Object} returns.* - All properties from the useSortable hook
 */
const useSortableRow = <RowType extends object, CustomPropsType extends object | undefined, ColumnIdType extends string | void>({
  id,
  sortable,
  columns,
  disableSort,
  disableSortTooltip,
}: UseSortableRowParams<RowType, CustomPropsType, ColumnIdType>) => {
  const sortableHook = useSortable({ id: id, disabled: !sortable || !!disableSort })

  const sortableColumns = useMemo(() => {
    const dragHandleColumn = {
      width: 60,
      accessor: () => {
        return (
          <GRTooltip content={disableSortTooltip} hidden={!disableSort}>
            <IconButton color="primary" {...sortableHook.listeners} {...sortableHook.attributes} style={{ cursor: 'grab' }} disabled={disableSort}>
              <DragIndicator />
            </IconButton>
          </GRTooltip>
        )
      },
    }

    return sortable ? ([dragHandleColumn, ...columns] as GRTableColumn<RowType, CustomPropsType, ColumnIdType>[]) : columns
  }, [sortable, columns, sortableHook.listeners, sortableHook.attributes, disableSort, disableSortTooltip])

  return { ...sortableHook, sortableColumns }
}
